import { useEffect, useRef, useState } from "react";

import { multiPolygon, polygon } from "@turf/helpers";
import simplify from "@turf/simplify";
import transformScale from "@turf/transform-scale";
import { Feature, MultiPolygon, Polygon } from "geojson";

import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { GeoMultiPolygon, GeoPolygon } from "@mapmycustomers/shared/types/shapes";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";
import {
  removeHolesFromMultiPolygon,
  removeHolesFromPolygon,
  removeSmallHolesFromMultiPolygon,
  removeSmallHolesFromPolygon,
} from "@mapmycustomers/shared/util/territory/removeHoles";

import { useMap, useMapEventHandler } from "@app/component/map/FeaturedMap";
import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

export const localCompoundScaleRatio = 1.001;

const unionWorker = new Worker(new URL("./unionWorker.worker", import.meta.url), {
  type: "module",
});

const debounceOptions = { leading: true, maxWait: 1000, trailing: true };

const generateShape = async (regions: AreaRegion[]) => {
  const selectedPolygons = regions.map((region) => {
    const processedRegion =
      region.geometry.type === "MultiPolygon"
        ? multiPolygon(removeHolesFromMultiPolygon(region.geometry.coordinates))
        : polygon(removeHolesFromPolygon(region.geometry.coordinates));
    // making each region slightly bigger to guarantee they would intersect along borders
    transformScale(processedRegion, localCompoundScaleRatio, { mutate: true, origin: "centroid" });
    simplify(processedRegion, { highQuality: true, mutate: true, tolerance: 0.001 });
    return processedRegion;
  });
  const combinedPolygon: Feature<MultiPolygon | Polygon> = await new Promise((resolve) => {
    unionWorker.onmessage = ({ data }) => resolve(data);
    unionWorker.postMessage({ selectedPolygons });
  });
  if (!combinedPolygon) {
    return undefined;
  }
  transformScale(combinedPolygon, 1 / localCompoundScaleRatio, {
    mutate: true,
    origin: "centroid",
  });

  simplify(combinedPolygon.geometry, {
    highQuality: true,
    mutate: true,
    tolerance: 0.001,
  });

  // now remove holes, but only small ones, which are most probably just union artefacts
  if (combinedPolygon.geometry.type === "Polygon") {
    combinedPolygon.geometry.coordinates = removeSmallHolesFromPolygon(
      combinedPolygon.geometry.coordinates as LongLat[][]
    );
  } else {
    combinedPolygon.geometry.coordinates = removeSmallHolesFromMultiPolygon(
      combinedPolygon.geometry.coordinates as LongLat[][][]
    );
  }

  return combinedPolygon;
};

const useBackendCompoundPolygon = (
  areaType: AreaType,
  selectedRegionIds: AreaRegion["id"][],
  currentLevel: TerritoryLevel,
  currentRegions: AreaRegion[]
): [
  polygon: GeoMultiPolygon | GeoPolygon | undefined,
  loading: boolean,
  loadedRegionIds: AreaRegion["id"][]
] => {
  const [loading, setLoading] = useState(false);
  const [compoundRegion, setCompoundRegion] = useState<GeoMultiPolygon | GeoPolygon | undefined>();
  const { map } = useMap();
  const requestRef = useRef<AbortController | undefined>();
  const [loadedRegionIds, setLoadedRegionIds] = useState<AreaRegion["id"][]>([]);

  const fetchCompoundPolygon = useDebouncedCallback([
    (payload) => {
      let fetched = false;
      requestRef.current?.abort();
      requestRef.current = new AbortController();
      setLoading(true);
      const signal = requestRef.current?.signal;
      fetch(getBoundaryUrl("shape"), {
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        method: "POST",
        signal,
      })
        .then((response) => response.text())
        .then((text) => {
          fetched = true;
          setCompoundRegion(
            !text.length ? undefined : (JSON.parse(text) as GeoMultiPolygon | GeoPolygon)
          );
          setLoadedRegionIds(selectedRegionIds);
        })
        .catch(() => {
          // we don't care, especially about aborted requests
        })
        .finally(() => {
          // only set loading to false if it was not due to abortion
          // because if it was - there's another request running currently, so
          // we need to keep loading state
          if (!signal.aborted) {
            setLoading(false);
          }
        });

      // while loading request, try to generate local compound region
      // but only do that if currentLevel and target level match
      const parseResult = parseAreaType(areaType);
      if (!parseResult || parseResult.level !== currentLevel) {
        return;
      }
      const selectedRegionIdsSet = new Set(selectedRegionIds);
      const selectedRegions = currentRegions.filter(({ id }) => selectedRegionIdsSet.has(id));
      if (selectedRegions.length) {
        generateShape(selectedRegions).then((localUnion) => {
          if (!fetched && localUnion) {
            setCompoundRegion(localUnion.geometry as GeoMultiPolygon | GeoPolygon);
          }
        });
      }
    },
    200,
    debounceOptions,
  ]);

  const [bounds, setBounds] = useState(map.getBounds()!);
  const viewPortListener = useDebouncedCallback([
    () => {
      setBounds(map.getBounds()!);
    },
    250,
  ]);
  useMapEventHandler("bounds_changed", viewPortListener);

  useEffect(() => {
    const { east, north, south, west } = bounds.toJSON();
    fetchCompoundPolygon({
      areaType,
      selectedRegionIds,
      viewport: [west, south, east, north],
      zoom: map.getZoom()!,
    });
  }, [areaType, selectedRegionIds, fetchCompoundPolygon, map, bounds]);

  return [compoundRegion, loading, loadedRegionIds];
};

export default useBackendCompoundPolygon;
